import SessionStore from '@/State/SessionStore'
import StaffCollection from '@/State/Collections/StaffCollection'
import { makeObservable, observable, action, computed, runInAction } from 'mobx'
import { calculateBillingPreview } from './billing-preview-calculator'

class BillingPageStore {
    @observable isSubscribing = false
    @observable isUpdatingSubscription = false
    @observable billingPeriod = undefined
    @observable isFetchingPreview = false
    @observable subscriptionPreview = null

    // Enhanced preview state management
    @observable previewError = null
    @observable isCalculatingPreview = false
    @observable showPreview = false
    @observable previewType = null // 'new-subscription', 'period-change', or null

    // Plan lookup data for client-side calculations
    @observable planLookup = {}
    @observable couponLookup = {}
    @observable taxLookup = {}

    // Enhanced lookup key resolution data from backend
    @observable resolvedPrices = {} // Lookup key -> Stripe price ID mapping
    @observable priceResolutionStatus = 'idle' // 'idle', 'resolving', 'resolved', 'failed'
    @observable priceResolutionError = null

    // Observables for upcoming invoice data (current subscription)
    @observable nextPaymentDate = null
    @observable amountDue = 0
    @observable discount = 0
    @observable tax = 0
    @observable credit = 0
    @observable adjustments = 0
    @observable grandTotal = 0
    @observable remainingCredit = 0
    /** @type {any[]} */
    @observable staffBreakdown = []

    // Payment details from Stripe (merged from getFreshPaymentDetails)
    @observable paymentMethods = []
    @observable receiptEmail = null
    @observable creditBalance = 0
    @observable currency = 'usd'
    @observable stripeCustomerId = null

    constructor() {
        makeObservable(this)
    }

    /**
     * @param {any} data
     */
    @action.bound
    setBillingData(data) {
        if (!data) {
            return
        }

        // Handle new nested data structure from getBillingData endpoint
        const paymentData = data.currentPayment || data // Support both old and new formats

        // Extract payment data from nested structure or fallback to root level
        this.nextPaymentDate = paymentData.nextPaymentDate
            ? new Date(paymentData.nextPaymentDate)
            : null
        this.amountDue = paymentData.amountDue || 0
        this.discount = paymentData.discount || 0
        this.tax = paymentData.tax || 0
        this.credit = paymentData.credit || 0
        this.adjustments = paymentData.adjustments || 0
        this.grandTotal = paymentData.grandTotal || 0
        this.remainingCredit =
            data.remainingCredit || paymentData.remainingCredit || 0
        this.staffBreakdown = paymentData.staffBreakdown || []

        // Clear any preview state to show current subscription data
        this.clearPreview()

        // Update SessionStore subscription data if provided
        if (data.subscription && SessionStore.subscription) {
            // Update the subscription period in SessionStore to match server data
            // Use currentPeriod from server response, fallback to subscription.subscriptionPeriod
            const correctPeriod =
                data.currentPeriod || data.subscription.subscriptionPeriod
            if (
                correctPeriod &&
                correctPeriod !== SessionStore.subscription.subscriptionPeriod
            ) {
                SessionStore.subscription.subscriptionPeriod = correctPeriod
            }
        }

        // Set lookup data for client-side calculations if available
        if (data.planLookup) {
            this.planLookup = data.planLookup

            // Check if we should show initial preview for new users
            this.checkInitialPreviewDisplay()
        }
        if (data.couponLookup) {
            this.couponLookup = data.couponLookup
        }
        if (data.taxLookup) {
            this.taxLookup = data.taxLookup
        }

        // Handle new lookup key-resolved pricing data from backend
        if (data.resolvedPrices) {
            this.resolvedPrices = data.resolvedPrices
            this.priceResolutionStatus = 'resolved'
            this.priceResolutionError = null
        }

        // Handle price resolution status from backend
        if (data.priceResolutionStatus) {
            this.priceResolutionStatus = data.priceResolutionStatus
        }
        if (data.priceResolutionError) {
            this.priceResolutionError = data.priceResolutionError
        }

        // Set payment details (merged from getFreshPaymentDetails)
        if (data.paymentMethods) {
            this.paymentMethods = data.paymentMethods
        }
        if (data.contactEmail !== undefined) {
            this.receiptEmail = data.contactEmail
        } else if (data.receiptEmail !== undefined) {
            this.receiptEmail = data.receiptEmail
        }
        if (data.creditBalance !== undefined) {
            this.creditBalance = data.creditBalance
        }
        if (data.currency) {
            this.currency = data.currency
        }
        if (data.stripeCustomerId !== undefined) {
            this.stripeCustomerId = data.stripeCustomerId
        }
    }

    // Computed Properties (Task 5.4, 5.5, 5.6)

    /**
     * Available billing periods based on current subscription
     */
    @computed get availableBillingPeriods() {
        const subscription = SessionStore.subscription
        if (!subscription) return ['monthly', 'yearly']

        const hasYearlyPlans =
            subscription.yearlyAdminPlanId || subscription.yearlyStandardPlanId
        const hasMonthlyPlans =
            subscription.monthlyAdminPlanId ||
            subscription.monthlyStandardPlanId

        if (hasYearlyPlans && hasMonthlyPlans) {
            return ['monthly', 'yearly']
        } else if (hasYearlyPlans) {
            return ['yearly']
        } else if (hasMonthlyPlans) {
            return ['monthly']
        }

        return ['monthly', 'yearly'] // Default fallback
    }

    /**
     * Current subscription period from session
     */
    @computed get currentSubscriptionPeriod() {
        return SessionStore.subscription?.subscriptionPeriod || 'monthly'
    }

    /**
     * Whether the user has an active subscription
     */
    @computed get hasActiveSubscription() {
        return SessionStore.subscription?.hasSubscription || false
    }

    /**
     * Whether preview should be shown
     */
    @computed get shouldShowPreview() {
        return (
            this.showPreview &&
            (this.subscriptionPreview || this.isCalculatingPreview)
        )
    }

    /**
     * Whether billing period has changed from current subscription
     */
    @computed get hasBillingPeriodChanged() {
        if (!this.billingPeriod || !this.hasActiveSubscription) return false
        return this.billingPeriod !== this.currentSubscriptionPeriod
    }

    /**
     * Preview type based on current state
     */
    @computed get currentPreviewType() {
        if (!this.billingPeriod) return null

        if (!this.hasActiveSubscription) {
            return 'new-subscription'
        } else if (this.hasBillingPeriodChanged) {
            return 'period-change'
        }

        return null
    }

    /**
     * Whether we have pricing data available (either legacy planLookup or new resolvedPrices)
     */
    @computed get hasPricingData() {
        const hasLegacyPricing =
            this.planLookup && Object.keys(this.planLookup).length > 0
        const hasResolvedPricing =
            this.resolvedPrices && Object.keys(this.resolvedPrices).length > 0
        return hasLegacyPricing || hasResolvedPricing
    }

    /**
     * Whether price resolution is in progress or failed
     */
    @computed get isPriceResolutionPending() {
        return (
            this.priceResolutionStatus === 'resolving' ||
            this.priceResolutionStatus === 'idle'
        )
    }

    /**
     * Whether price resolution has failed
     */
    @computed get hasPriceResolutionFailed() {
        return this.priceResolutionStatus === 'failed'
    }

    /**
     * Staff breakdown with calculated fees - prefers server data when available
     */
    @computed get computedStaffBreakdown() {
        // Always prefer server data when available and current billing period matches subscription period
        // Server data comes from getBillingData and uses proper plan pricing
        const hasServerData =
            this.staffBreakdown && this.staffBreakdown.length > 0
        const isCurrentPeriod =
            !this.billingPeriod ||
            this.billingPeriod === this.currentSubscriptionPeriod

        if (hasServerData && isCurrentPeriod) {
            return this.staffBreakdown
        }

        // For preview scenarios (different billing period), use client-side calculation
        if (
            !StaffCollection.staff ||
            !this.planLookup ||
            Object.keys(this.planLookup).length === 0
        ) {
            return this.staffBreakdown || []
        }

        // Use client-side calculation for preview scenarios only
        try {
            const previewData = calculateBillingPreview(
                StaffCollection.staff,
                this.billingPeriod || this.currentSubscriptionPeriod,
                SessionStore.subscription,
                this.planLookup,
                this.couponLookup,
                this.taxLookup
            )
            return previewData.staffBreakdown
        } catch (error) {
            return this.staffBreakdown || []
        }
    }

    /**
     * Savings amount when switching to yearly
     */
    @computed get yearlySavings() {
        if (!this.subscriptionPreview || this.billingPeriod !== 'yearly')
            return 0

        try {
            // Calculate monthly equivalent
            const monthlyData = calculateBillingPreview(
                StaffCollection.staff || [],
                'monthly',
                SessionStore.subscription,
                this.planLookup,
                this.couponLookup,
                this.taxLookup
            )

            const yearlyTotal = this.subscriptionPreview.grandTotal
            const monthlyEquivalent = monthlyData.grandTotal * 12

            return Math.max(0, monthlyEquivalent - yearlyTotal)
        } catch (error) {
            console.warn('Failed to calculate yearly savings:', error)
            return 0
        }
    }

    /**
     * Computed grand total that follows standardized Total vs Credit Applied rules
     * Matches the PRD format: Date → Amount Due → Discounts → Adjustments → Tax → Credit → Total/Credit Applied
     */
    @computed get computedGrandTotal() {
        // Follow standardized calculation order from PRD
        const amountDue = this.amountDue || 0
        const discount = this.discount || 0
        const adjustments = this.adjustments || 0
        const tax = this.tax || 0
        const credit = Math.abs(this.credit || 0) // Credit should be positive for calculation

        // Calculate subtotal before credit application
        const subtotal = amountDue - discount + adjustments + tax

        // Apply PRD credit rules: if credit covers entire amount, return 0 (shows "Credit Applied")
        const result = subtotal - credit

        return result
    }

    /**
     * Computed credit that shows remaining credit after applying to current bill
     */
    @computed get computedRemainingCredit() {
        const subtotal =
            this.amountDue +
            this.tax +
            (this.adjustments || 0) -
            (this.discount || 0)
        const availableCredit = Math.abs(this.credit || 0) // Credit is usually negative

        if (availableCredit >= subtotal && subtotal > 0) {
            // Credit covers the entire amount, return remaining credit
            return -(availableCredit - subtotal) // Keep as negative to match expected format
        } else {
            // Credit partially applied or no credit remaining
            return this.credit
        }
    }

    /**
     * Computed grand total for preview that follows standardized Total vs Credit Applied rules
     * This matches the backend generateStandardizedBillingPreview() implementation
     */
    @computed get previewComputedGrandTotal() {
        if (!this.subscriptionPreview) {
            return 0
        }

        const preview = this.subscriptionPreview

        // Use the standardized grandTotal calculation from the preview
        // This already follows the PRD rules: when credit covers entire amount, grandTotal = 0
        if (preview.grandTotal !== undefined) {
            return preview.grandTotal
        }

        // Fallback calculation following standardized format:
        // Date → Amount Due → Discounts → Adjustments → Tax → Credit → Total/Credit Applied
        const amountDue = preview.amountDue || 0
        const discount = preview.discount || 0
        const adjustments = preview.adjustments || 0
        const tax = preview.tax || 0
        const credit = Math.abs(preview.credit || 0) // Credit should be positive for calculation

        // Calculate subtotal before credit application
        const subtotal = amountDue - discount + adjustments + tax

        // Apply PRD credit rules: if credit covers entire amount, return 0 (shows "Credit Applied")
        const result =
            subtotal <= credit && subtotal > 0
                ? 0
                : Math.max(0, subtotal - credit)

        return result
    }

    /**
     * @param {boolean} isSubscribing
     */
    @action.bound
    setIsSubscribing(isSubscribing) {
        this.isSubscribing = isSubscribing
    }

    @action.bound
    setIsUpdatingSubscription(isUpdatingSubscription) {
        this.isUpdatingSubscription = isUpdatingSubscription
    }

    @action.bound
    setIsFetchingPreview(isFetchingPreview) {
        this.isFetchingPreview = isFetchingPreview
    }

    @action.bound
    setSubscriptionPreview(subscriptionPreview) {
        this.subscriptionPreview = subscriptionPreview
    }

    /**
     * @param {'monthly' | 'yearly'} billingPeriod
     */
    @action.bound
    setBillingPeriod(billingPeriod) {
        // If the billing period matches current subscription, clear preview to show default view
        if (
            this.hasActiveSubscription &&
            billingPeriod === this.currentSubscriptionPeriod
        ) {
            this.clearPreview()
            return
        }

        this.billingPeriod = billingPeriod

        // Update preview type and trigger calculation if needed
        this.previewType = this.currentPreviewType

        if (
            billingPeriod &&
            this.planLookup &&
            Object.keys(this.planLookup).length > 0
        ) {
            this.calculatePreview()
        }
    }

    // Enhanced Actions (Task 5.7)

    @action.bound
    setPreviewError(error) {
        this.previewError = error
    }

    @action.bound
    setIsCalculatingPreview(isCalculating) {
        this.isCalculatingPreview = isCalculating
    }

    @action.bound
    setShowPreview(show) {
        this.showPreview = show
    }

    @action.bound
    setPreviewType(type) {
        this.previewType = type
    }

    @action.bound
    setPlanLookup(planLookup) {
        this.planLookup = planLookup
    }

    @action.bound
    setCouponLookup(couponLookup) {
        this.couponLookup = couponLookup
    }

    @action.bound
    setTaxLookup(taxLookup) {
        this.taxLookup = taxLookup
    }

    @action.bound
    setResolvedPrices(resolvedPrices) {
        this.resolvedPrices = resolvedPrices
        this.priceResolutionStatus = 'resolved'
        this.priceResolutionError = null
    }

    @action.bound
    setPriceResolutionStatus(status) {
        this.priceResolutionStatus = status
    }

    @action.bound
    setPriceResolutionError(error) {
        this.priceResolutionError = error
        this.priceResolutionStatus = 'failed'
    }

    /**
     * Calculate billing preview using client-side calculator
     */
    @action.bound
    async calculatePreview() {
        if (!this.billingPeriod || !StaffCollection.staff) {
            return
        }

        // Set calculating state
        runInAction(() => {
            this.isCalculatingPreview = true
            this.isFetchingPreview = true
            this.previewError = null
            this.showPreview = true
        })

        try {
            // Check for price resolution issues first
            if (this.hasPriceResolutionFailed) {
                throw new Error(
                    this.priceResolutionError ||
                        'Price resolution failed. Please try refreshing the page.'
                )
            }

            // Always prioritize server preview for accurate data (includes stored plan preferences and prorations)
            if (this.hasActiveSubscription && this.hasBillingPeriodChanged) {
                await this.fetchServerPreview()
            } else if (!this.hasActiveSubscription) {
                await this.fetchNewSubscriptionPreview()
            } else if (this.hasPricingData) {
                // Use standardized client-side calculation only for current period view
                const previewData = calculateBillingPreview(
                    StaffCollection.staff,
                    this.billingPeriod,
                    SessionStore.subscription,
                    this.planLookup,
                    this.couponLookup,
                    this.taxLookup
                )

                runInAction(() => {
                    this.subscriptionPreview = previewData
                    this.previewType = this.currentPreviewType
                })
            } else if (this.isPriceResolutionPending) {
                // Still waiting for price resolution
                throw new Error('Loading pricing data... Please wait.')
            } else {
                throw new Error(
                    'No pricing data available. Please contact support.'
                )
            }
        } catch (error) {
            // Enhanced error categorization for billing preview failures
            const errorMessage = error.message || 'Failed to calculate preview'
            const isCouponError =
                errorMessage.toLowerCase().includes('coupon') ||
                errorMessage.toLowerCase().includes('validation') ||
                errorMessage.toLowerCase().includes('discount')
            const isCalculationError =
                errorMessage.toLowerCase().includes('calculation') ||
                errorMessage.toLowerCase().includes('standardized')

            // Enhanced error logging for monitoring
            if (window.Sentry) {
                window.Sentry.captureException(error, {
                    tags: {
                        operation: 'calculatePreview',
                        errorType: isCouponError
                            ? 'coupon_validation'
                            : isCalculationError
                              ? 'calculation_error'
                              : 'general',
                        billingPeriod: this.billingPeriod,
                        previewType: this.previewType,
                        hasPricingData: this.hasPricingData,
                    },
                })
            }
            if (window.posthog) {
                window.posthog.captureException(error)
                window.posthog.capture('billing_preview_calculation_failed', {
                    error: errorMessage,
                    errorType: isCouponError
                        ? 'coupon_validation'
                        : isCalculationError
                          ? 'calculation_error'
                          : 'general',
                    billingPeriod: this.billingPeriod,
                    organisationId: SessionStore.organisation?.id,
                })
            }

            runInAction(() => {
                // Provide specific error messaging based on error type
                if (isCouponError) {
                    this.previewError = `Preview unavailable: ${errorMessage}. Please contact support for assistance.`
                } else if (isCalculationError) {
                    this.previewError = `Unable to calculate billing preview. Please try again or contact support.`
                } else {
                    this.previewError = errorMessage
                }
                this.subscriptionPreview = null
            })
        } finally {
            runInAction(() => {
                this.isCalculatingPreview = false
                this.isFetchingPreview = false
            })
        }
    }

    /**
     * Check if we should display initial preview for new users
     */
    @action.bound
    checkInitialPreviewDisplay() {
        // For new users (no active subscription), show monthly preview by default
        if (!this.hasActiveSubscription && !this.billingPeriod) {
            this.billingPeriod = 'monthly'
        }

        // Trigger preview calculation if we have all required data
        if (
            this.billingPeriod &&
            StaffCollection.staff?.length > 0 &&
            this.hasPricingData
        ) {
            // Use setTimeout to ensure MobX observable updates are processed
            setTimeout(() => {
                this.calculatePreview()
            }, 0)
        }
    }

    /**
     * Call this when staff data becomes available to check for initial preview
     */
    @action.bound
    onStaffDataAvailable() {
        // Check if we should show initial preview now that staff data is available
        this.checkInitialPreviewDisplay()
    }

    /**
     * Fetch preview from server for new subscriptions (uses stored plan preferences)
     */
    @action.bound
    async fetchNewSubscriptionPreview() {
        try {
            const { trpc } = await import('../../system/trpc')

            // Call tRPC endpoint for server-side preview calculation with stored plan preferences
            const serverPreview = await trpc.legacy.previewBillingChange.query({
                targetPeriod: this.billingPeriod,
            })

            // Extract the preview data from the nested response structure
            const previewData = {
                ...serverPreview.preview,
                // Add additional properties from the response
                currentPeriod: serverPreview.currentPeriod,
                targetPeriod: serverPreview.targetPeriod,
                warnings: serverPreview.warnings,
                breakdown: serverPreview.breakdown,
            }

            // Update preview with server calculation (includes stored plan preferences)
            runInAction(() => {
                this.subscriptionPreview = previewData
                this.previewType = this.currentPreviewType
            })
        } catch (error) {
            // For new subscriptions, server preview is important for stored plan preferences
            // Fallback to client calculation as a last resort
            if (this.hasPricingData) {
                const previewData = calculateBillingPreview(
                    StaffCollection.staff,
                    this.billingPeriod,
                    SessionStore.subscription,
                    this.planLookup,
                    this.couponLookup,
                    this.taxLookup
                )

                runInAction(() => {
                    this.subscriptionPreview = previewData
                    this.previewType = this.currentPreviewType
                })
            } else {
                throw error
            }
        }
    }

    /**
     * Fetch preview from server for billing period changes
     */
    @action.bound
    async fetchServerPreview() {
        if (!this.hasBillingPeriodChanged) return

        try {
            const { trpc } = await import('../../system/trpc')

            // Call tRPC endpoint for server-side preview calculation with prorations
            const serverPreview = await trpc.legacy.previewBillingChange.query({
                targetPeriod: this.billingPeriod,
            })

            // Extract the preview data from the nested response structure
            const previewData = {
                ...serverPreview.preview,
                // Add additional properties from the response
                currentPeriod: serverPreview.currentPeriod,
                targetPeriod: serverPreview.targetPeriod,
                warnings: serverPreview.warnings,
                breakdown: serverPreview.breakdown,
            }

            // Update preview with server calculation (includes accurate prorations)
            runInAction(() => {
                this.subscriptionPreview = previewData
                this.previewType = this.currentPreviewType
            })
        } catch (error) {
            // For billing period changes, server preview is critical (contains prorations)
            // Don't fallback to client calculation as it lacks proration data
            throw new Error(
                `Unable to calculate billing period change preview: ${error.message || 'Server error'}. Prorations and adjustments require server calculation.`
            )
        }
    }

    /**
     * Clear preview state
     */
    @action.bound
    clearPreview() {
        this.subscriptionPreview = null
        this.showPreview = false
        this.previewError = null
        this.isCalculatingPreview = false
        this.isFetchingPreview = false
        this.previewType = null
        // Reset billing period to undefined to return to default state
        this.billingPeriod = undefined
    }

    /**
     * Reset billing period to current subscription period
     */
    @action.bound
    resetBillingPeriod() {
        this.billingPeriod = this.currentSubscriptionPeriod
        this.clearPreview()
    }

    /**
     * Start new subscription process using checkout sessions
     */
    @action.bound
    async startNewSubscription() {
        if (!this.billingPeriod || !this.subscriptionPreview) {
            throw new Error('No billing period or preview data available')
        }

        try {
            runInAction(() => {
                this.isSubscribing = true
                this.previewError = null
            })

            // Track checkout session creation attempt
            if (window.posthog) {
                window.posthog.capture('checkout_session_creation_started', {
                    period: this.billingPeriod,
                    organisationId: SessionStore.organisation?.id,
                    source: 'billing_page_toggle',
                })
            }

            // Import trpc dynamically
            const { trpc } = await import('../../system/trpc')

            // Create checkout session using tRPC
            const result = await trpc.legacy.createCheckoutSession.mutate({
                period: this.billingPeriod,
            })

            if (result.success && result.sessionUrl) {
                // Track successful session creation
                if (window.posthog) {
                    window.posthog.capture('checkout_session_created', {
                        sessionId: result.sessionId,
                        period: this.billingPeriod,
                        organisationId: SessionStore.organisation?.id,
                        source: 'billing_page_toggle',
                    })
                }

                // Clear preview state before redirecting
                this.clearPreview()

                // Redirect to Stripe Checkout
                window.location.href = result.sessionUrl
            } else {
                throw new Error(
                    result.message || 'Failed to create checkout session'
                )
            }
        } catch (error) {
            // Enhanced error categorization for better user feedback
            const errorMessage =
                error.message || 'Failed to start subscription process'
            const isCouponError =
                errorMessage.toLowerCase().includes('coupon') ||
                errorMessage.toLowerCase().includes('validation') ||
                errorMessage.toLowerCase().includes('discount')

            // Track checkout session creation failure with error categorization
            if (window.posthog) {
                window.posthog.capture('checkout_session_creation_failed', {
                    error: errorMessage,
                    errorType: isCouponError ? 'coupon_validation' : 'general',
                    period: this.billingPeriod,
                    organisationId: SessionStore.organisation?.id,
                    source: 'billing_page_toggle',
                })
            }

            // Capture error for monitoring with additional context
            if (window.Sentry) {
                window.Sentry.captureException(error, {
                    tags: {
                        operation: 'startNewSubscription',
                        errorType: isCouponError
                            ? 'coupon_validation'
                            : 'general',
                        billingPeriod: this.billingPeriod,
                    },
                })
            }
            if (window.posthog) {
                window.posthog.captureException(error)
            }

            runInAction(() => {
                // Provide more specific error messaging for coupon issues
                this.previewError = isCouponError
                    ? `Billing setup issue: ${errorMessage}. Please contact support for assistance.`
                    : errorMessage
                this.isSubscribing = false
            })
        }
    }

    /**
     * Confirm billing period change for existing subscription using updateSubscription
     */
    @action.bound
    async confirmBillingPeriodChange() {
        if (!this.hasBillingPeriodChanged || !this.subscriptionPreview) {
            throw new Error('No billing period change to confirm')
        }

        try {
            runInAction(() => {
                this.isUpdatingSubscription = true
                this.previewError = null
            })

            // Track subscription update attempt
            if (window.posthog) {
                window.posthog.capture('subscription_update_started', {
                    newPeriod: this.billingPeriod,
                    organisationId: SessionStore.organisation?.id,
                    source: 'billing_page_toggle',
                })
            }

            // Import trpc dynamically
            const { trpc } = await import('../../system/trpc')

            // Update subscription directly using tRPC
            const result = await trpc.legacy.updateSubscription.mutate({
                newPeriod: this.billingPeriod,
            })

            if (result.success) {
                // Track successful subscription update
                if (window.posthog) {
                    window.posthog.capture('subscription_updated', {
                        subscriptionId: result.subscriptionId,
                        newPeriod: this.billingPeriod,
                        organisationId: SessionStore.organisation?.id,
                        source: 'billing_page_toggle',
                    })
                }

                // Clear preview state after successful update
                this.clearPreview()

                // Refresh billing data to show updated subscription
                const billingData = await trpc.legacy.getBillingData.query()
                this.setBillingData(billingData)

                // Also ensure SessionStore subscription period is updated
                if (SessionStore.subscription && billingData.currentPeriod) {
                    SessionStore.subscription.subscriptionPeriod =
                        billingData.currentPeriod
                }

                // Optional: Show a toast notification or update UI to indicate success
                if (window.Sentry) {
                    window.Sentry.addBreadcrumb({
                        message: 'Subscription period updated successfully',
                        category: 'billing',
                        level: 'info',
                        data: {
                            newPeriod: this.billingPeriod,
                            subscriptionId: result.subscriptionId,
                        },
                    })
                }
            } else {
                throw new Error(
                    result.message || 'Failed to update subscription'
                )
            }
        } catch (error) {
            // Enhanced error categorization for better user feedback
            const errorMessage =
                error.message || 'Failed to update subscription'
            const isCouponError =
                errorMessage.toLowerCase().includes('coupon') ||
                errorMessage.toLowerCase().includes('validation') ||
                errorMessage.toLowerCase().includes('discount')

            // Track subscription update failure with error categorization
            if (window.posthog) {
                window.posthog.capture('subscription_update_failed', {
                    error: errorMessage,
                    errorType: isCouponError ? 'coupon_validation' : 'general',
                    newPeriod: this.billingPeriod,
                    organisationId: SessionStore.organisation?.id,
                    source: 'billing_page_toggle',
                })
            }

            // Capture error for monitoring with additional context
            if (window.Sentry) {
                window.Sentry.captureException(error, {
                    tags: {
                        operation: 'confirmBillingPeriodChange',
                        errorType: isCouponError
                            ? 'coupon_validation'
                            : 'general',
                        billingPeriod: this.billingPeriod,
                    },
                })
            }
            if (window.posthog) {
                window.posthog.captureException(error)
            }

            runInAction(() => {
                // Provide more specific error messaging for coupon issues
                this.previewError = isCouponError
                    ? `Billing update issue: ${errorMessage}. Please contact support for assistance.`
                    : errorMessage
                this.isUpdatingSubscription = false
            })
        } finally {
            runInAction(() => {
                this.isUpdatingSubscription = false
            })
        }
    }
}

export default new BillingPageStore()
